/** Ferramentas de montagem: peças coloridas fundidas numa malha só, com contorno por casco invertido. */
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { INK, ramp } from "@besenliga/athlete-three";
import { nextFloat, seedRng, type RngState } from "@besenliga/shared";

/** Gerador com semente: o estádio sai igual em toda máquina e em toda captura. */
export class Random {
  private state: RngState;
  constructor(seed: number) {
    this.state = seedRng(seed);
  }
  next(): number {
    const [s, f] = nextFloat(this.state);
    this.state = s;
    return f;
  }
  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }
  pick<T>(items: readonly T[]): T {
    const item = items[Math.min(items.length - 1, Math.floor(this.next() * items.length))];
    if (item === undefined) throw new Error("pick sem opções");
    return item;
  }
}

export function toonMaterial(opts: THREE.MeshToonMaterialParameters = {}): THREE.MeshToonMaterial {
  return new THREE.MeshToonMaterial({ gradientMap: ramp, ...opts });
}

export const outlineMaterial = new THREE.MeshBasicMaterial({ color: INK, side: THREE.BackSide });

function paint(geo: THREE.BufferGeometry, color: THREE.Color): THREE.BufferGeometry {
  const n = geo.getAttribute("position").count;
  const colors = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) color.toArray(colors, i * 3);
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geo;
}

function onlyPosition(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  for (const name of Object.keys(geo.attributes)) if (name !== "position") geo.deleteAttribute(name);
  return geo;
}

export interface PartOptions {
  /** Escala do casco do contorno em relação à peça (0 = sem contorno). */
  outline?: number;
  /** Contorno mais fino na vertical (bom para cilindros baixos). */
  radial?: boolean;
}

/** Junta peças (cada uma com sua cor e transformação) em até duas malhas: a peça e o contorno. */
export class PartBuilder {
  private readonly parts: THREE.BufferGeometry[] = [];
  private readonly hulls: THREE.BufferGeometry[] = [];
  private readonly color = new THREE.Color();

  add(
    geo: THREE.BufferGeometry,
    color: number,
    matrix: THREE.Matrix4,
    { outline = 1.06, radial = false }: PartOptions = {},
  ): void {
    const part = geo.clone();
    part.deleteAttribute("uv");
    part.applyMatrix4(matrix);
    this.parts.push(paint(part, this.color.setHex(color)));
    if (outline > 0) {
      const hull = onlyPosition(geo.clone());
      hull.scale(outline, radial ? 1 + (outline - 1) * 0.15 : outline, outline);
      hull.applyMatrix4(matrix);
      this.hulls.push(hull);
    }
    geo.dispose();
  }

  /** Cria as malhas. `material` precisa de `vertexColors: true`. */
  build(
    name: string,
    material: THREE.Material,
    { castShadow = true, receiveShadow = false } = {},
  ): THREE.Group {
    const group = new THREE.Group();
    group.name = name;
    if (this.parts.length) {
      const mesh = new THREE.Mesh(merge(this.parts), material);
      mesh.name = name;
      mesh.castShadow = castShadow;
      mesh.receiveShadow = receiveShadow;
      group.add(mesh);
    }
    if (this.hulls.length) {
      const hull = new THREE.Mesh(merge(this.hulls), outlineMaterial);
      hull.name = `${name}_contorno`;
      group.add(hull);
    }
    return group;
  }
}

export function merge(geos: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const merged = mergeGeometries(geos, false);
  if (!merged) throw new Error("Falha ao fundir geometrias");
  for (const g of geos) g.dispose();
  merged.computeBoundingSphere();
  return merged;
}

const tmp = {
  pos: new THREE.Vector3(),
  quat: new THREE.Quaternion(),
  scale: new THREE.Vector3(),
  euler: new THREE.Euler(),
};

/** Matriz a partir de posição, rotação (Euler) e escala. */
export function at(
  x: number,
  y: number,
  z: number,
  rot: [number, number, number] = [0, 0, 0],
  scale: number | [number, number, number] = 1,
): THREE.Matrix4 {
  tmp.pos.set(x, y, z);
  tmp.quat.setFromEuler(tmp.euler.set(...rot));
  if (typeof scale === "number") tmp.scale.setScalar(scale);
  else tmp.scale.set(...scale);
  return new THREE.Matrix4().compose(tmp.pos, tmp.quat, tmp.scale);
}

/**
 * Faixa no chão (plano XZ) ao longo de um arco de elipse centrado em (cx, cz).
 * `inner`/`outer` são os semieixos [x, z] das bordas de dentro e de fora.
 */
export function ellipseBand(
  inner: [number, number],
  outer: [number, number],
  { cx = 0, cz = 0, from = 0, to = Math.PI * 2, segments = 128, y = 0 } = {},
): THREE.BufferGeometry {
  const pos: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const a = from + ((to - from) * i) / segments;
    const c = Math.cos(a);
    const s = Math.sin(a);
    pos.push(cx + c * inner[0], y, cz + s * inner[1], cx + c * outer[0], y, cz + s * outer[1]);
    if (i < segments) {
      const k = i * 2;
      idx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  // Garante a face para cima, qualquer que seja o sentido do arco.
  if (g.getAttribute("normal").getY(1) < 0) {
    g.setIndex(idx.map((_, i, arr) => arr[i - (i % 3) + 2 - (i % 3)] ?? 0));
    g.computeVertexNormals();
  }
  return g;
}

/** Retângulo no chão, de (x0, z0) a (x1, z1). */
export function groundRect(x0: number, z0: number, x1: number, z1: number, y = 0): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(x1 - x0, z1 - z0);
  g.rotateX(-Math.PI / 2);
  g.translate((x0 + x1) / 2, y, (z0 + z1) / 2);
  g.deleteAttribute("uv");
  return g;
}
